import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button } from '@material-ui/core';
import { UploadCloudIcon } from '../../assets/icons';
import { uploadFile } from '../../Services';
import { getDownloadableLink, showError } from '../../Helper';
import { LoadableImageComponant } from '../LoadableImageComponant/LoadableImageComponant';
import { MediaPreviewDialog } from '../../Views/Home/TemplatesView/Dialogs/TemplatesPreviewDialog/MediaPreviewDialog';
import './FilesUploader.scss';

const parentTranslationPath = 'FilesUploader';
const translationPath = '';
function FilesUploader({
  accept = 'media/*',
  multiple = false,
  maxSize = 80000000,
  initUploadedFiles = [],
  uploadedChanged,
  disabled = false,
  className = '',
  setIsUploading,
  onItemRemoved,
  uploadingHandler,
}) {
  const { t: translate } = useTranslation(parentTranslationPath);
  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploadingLocal, setIsUploadingLocal] = useState(false);
  const [allFiles, setAllFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [viewedFile, setViewedFile] = useState(null);
  const [isOpenPreviewDialog, setIsOpenPreviewDialog] = useState(false);
  const validateFile = (file) => {
    if (file.size > maxSize) {
      return {
        valid: false,
        error: translate('fileSizeTooLargeError'),
      };
    }

    return { valid: true };
  };

  const uploadHandler = (files) => {
    setIsUploadingLocal(true);
    if (setIsUploading) setIsUploading(true);

    let completedUploads = 0;
    const successfullyUploadedFiles = [];

    files.map((item, index) => {
      uploadFile({ file: item.file })
        .then((response) => {
          if (multiple) uploadedFiles.push(response);
          const localUploadedFiles = (multiple && uploadedFiles) || [
            { ...response },
          ];
          setUploadedFiles(localUploadedFiles);
          if (uploadedChanged) uploadedChanged(localUploadedFiles);
          setAllFiles((items) => {
            const fileIndex = items.findIndex(
              (element) => element.id === item.id
            );
            if (fileIndex !== -1) {
              items[fileIndex].uuid = response.uuid;
              items[fileIndex].status = 'success';
              successfullyUploadedFiles.push({
                ...items[fileIndex],
                uuid: response.uuid,
                status: 'success',
              });
            }
            return [...items];
          });
        })
        .catch(() => {
          setAllFiles((items) => {
            const fileIndex = items.findIndex(
              (element) => element.id === item.id
            );
            if (fileIndex !== -1) {
              items[fileIndex].status = 'failed';
            }
            return [...items];
          });
        })
        .finally(() => {
          completedUploads++;
          if (completedUploads === files.length) {
            setIsUploadingLocal(false);
            if (setIsUploading) setIsUploading(false);
            if (uploadingHandler && successfullyUploadedFiles.length > 0) {
              uploadingHandler(successfullyUploadedFiles);
            }
          }
        });
      return undefined;
    });
  };

  const handleFiles = (files) => {
    const fileArray = Array.from(files);
    const validFiles = [];
    const errors = [];

    fileArray.forEach((file) => {
      const validation = validateFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        errors.push(`${file.name}: ${validation.error}`);
      }
    });

    if (validFiles.length > 0) {
      let newFiles = [];
      if (multiple) {
        validFiles.forEach((file) => {
          newFiles.push({
            id: allFiles.length + newFiles.length,
            uuid: null,
            fileName: file.name,
            size: file.size,
            type: file.type,
            file,
            status: 'uploading',
          });
        });
      } else {
        newFiles = [
          {
            id: allFiles.length,
            uuid: null,
            fileName: validFiles[0].name,
            size: validFiles[0].size,
            type: validFiles[0].type,
            file: validFiles[0],
            status: 'uploading',
          },
        ];
      }
      setAllFiles((items) => (multiple && items.concat(newFiles)) || newFiles);
      uploadHandler(newFiles);
    }

    if (errors.length > 0) {
      errors.forEach((error) => showError(error));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFiles(files);
    }
  };

  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      handleFiles(files);
    }
    e.target.value = '';
  };

  const handleBrowseClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const removeFile = (file, index) => {
    const uploadedFilesIndex = uploadedFiles.findIndex(
      (element) => element.uuid || file.fileId === file.uuid || file.fileId
    );
    if (uploadedFilesIndex !== -1) {
      const localFiles = [...uploadedFiles];
      localFiles.splice(uploadedFilesIndex, 1);
      if (uploadedChanged) uploadedChanged(localFiles);
      setUploadedFiles(localFiles);
    }

    if (onItemRemoved) onItemRemoved(file);

    setAllFiles((items) => {
      const newItems = [...items];
      newItems.splice(index, 1);
      return newItems;
    });
  };

  const downloadFile = (file) => {
    if (file.uuid || file.fileId) {
      const downloadUrl = getDownloadableLink(file.uuid || file.fileId);
      const link = document.createElement('a');
      link.setAttribute('download', file.fileName || 'download');
      link.href = downloadUrl;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } else if (
      file.file &&
      file.status !== 'uploading' &&
      file.status !== 'failed'
    ) {
      const url = URL.createObjectURL(file.file);
      const link = document.createElement('a');
      link.setAttribute('download', file.fileName || 'download');
      link.href = url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const viewFile = (file) => {
    setViewedFile({
      templateFileId: file.uuid || file.fileId,
      templateFileName: file.fileName,
      templateText: '',
    });
    setIsOpenPreviewDialog(true);
  };

  useEffect(() => {
    if (initUploadedFiles && initUploadedFiles.length >= 0) {
      setUploadedFiles(initUploadedFiles);
      setAllFiles(initUploadedFiles);
    }
  }, [initUploadedFiles]);

  return (
    <div className={`files-uploader-wrapper ${className}`}>
      <div
        className={`files-uploader-dropzone ${isDragOver ? 'drag-over' : ''} ${
          disabled ? 'disabled' : ''
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type='file'
          accept={accept}
          multiple={multiple}
          onChange={handleFileInputChange}
          disabled={disabled}
          style={{ display: 'none' }}
        />

        <div className='upload-content'>
          <div className='upload-icon'>
            <UploadCloudIcon width={48} height={48} fill='#6B7280' />
          </div>

          <div className='upload-text'>
            <span className='upload-main-text'>
              <Button
                className='upload-link-button'
                onClick={handleBrowseClick}
                disabled={disabled}
              >
                {translate(`click-to-upload`)}
              </Button>{' '}
              {translate(`or-drag-and-drop`)}
            </span>
            <span className='upload-sub-text'>
              JPG, PNG, MP4, SVG, GIF, PDF, or TXT
            </span>
          </div>
        </div>

        {isUploadingLocal && (
          <div className='upload-spinner'>
            <span className='mdi mdi-reload mdi-spin' />
          </div>
        )}
      </div>

      {allFiles.length > 0 && (
        <div className='uploaded-files-gallery'>
          {allFiles.map((file, index) => (
            <div
              key={file.id || index}
              className='uploaded-file-card clickable'
              onClick={() => viewFile(file)}
              title={`Click to view ${file.fileName}`}
            >
              <div className='file-preview'>
                {file.status === 'uploading' && (
                  <div className='upload-overlay'>
                    <span className='mdi mdi-reload mdi-spin' />
                  </div>
                )}
                {file.status === 'failed' && (
                  <div className='upload-overlay failed'>
                    <span className='mdi mdi-alert-circle' />
                  </div>
                )}
                {(file.status === 'success' || file.uuid || file.fileId) && (
                  <LoadableImageComponant
                    src={getDownloadableLink(file.uuid || file.fileId)}
                    classes='uploaded-image'
                    alt={file.fileName}
                  />
                )}
                {!file.uuid ||
                  (!file.fileId &&
                    file.file &&
                    file.status !== 'uploading' &&
                    file.status !== 'failed' && (
                      <img
                        src={URL.createObjectURL(file.file)}
                        alt={file.fileName}
                        className='uploaded-image'
                      />
                    ))}

                <div className='file-actions'>
                  <Button
                    className='remove-file-btn'
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(file, index);
                    }}
                    disabled={disabled}
                    title='Remove file'
                  >
                    <span className='mdi mdi-close' />
                  </Button>

                  <Button
                    className='view-file-btn'
                    onClick={(e) => {
                      e.stopPropagation();
                      viewFile(file);
                    }}
                    disabled={disabled || (!file.uuid && !file.fileId)}
                    title='View file'
                  >
                    <span className='mdi mdi-eye' />
                  </Button>

                  <Button
                    className='download-file-btn'
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadFile(file);
                    }}
                    disabled={
                      disabled ||
                      (!file.uuid &&
                        !file.fileId &&
                        file.status === 'uploading')
                    }
                    title='Download file'
                  >
                    <span className='mdi mdi-download' />
                  </Button>
                </div>
              </div>
              <div className='file-info'>
                <span className='file-name' title={file.fileName}>
                  {file.fileName}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {isOpenPreviewDialog && (
        <MediaPreviewDialog
          activeItem={viewedFile}
          maintitleText='FILE-VIEW'
          isOpen={isOpenPreviewDialog}
          isOpenChanged={() => {
            setIsOpenPreviewDialog(false);
            setViewedFile(null);
          }}
          parentTranslationPath='Shared'
          translationPath={translationPath}
        />
      )}
    </div>
  );
}

FilesUploader.propTypes = {
  accept: PropTypes.string,
  multiple: PropTypes.bool,
  maxSize: PropTypes.number,
  initUploadedFiles: PropTypes.array,
  uploadedChanged: PropTypes.func,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  translationPath: PropTypes.string,
  parentTranslationPath: PropTypes.string,
  translationPathShared: PropTypes.string,
  setIsUploading: PropTypes.func,
};

export default FilesUploader;
